import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { RedirectService } from '../../shared/services/redirect.service';
import { ENV } from '../../environment';
import { FeatureService } from '../../install/shared/services/feature.service';
import { Observable } from 'rxjs';
import { INSTALLATION_FINISH_STATUS } from '../../install/shared/navigation/constants';
import { of } from 'rxjs/observable/of';
import { catchError, concatMap, filter, map } from 'rxjs/operators';
import { AuthService } from '@yotpo-ui/core';

@Injectable()
export class CompleteGuard implements CanActivate {

  constructor(
    private authService: AuthService,
    private redirectService: RedirectService,
    private featureService: FeatureService,
    private router: Router
  ) { }

  // if new_signup is presented in the URL we got here from a platform redirect, in that case do not check storage values
  // if appkey is presented in the URL, we got here from application redirect (such as loyalty), in that case ignore storage values
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const platform = route.queryParams['platform'] || route.queryParams['platform_id'];
    const product = route.queryParams['product'];
    if (this.shouldRedirectToNewSignupApp(platform, product)) {
      this.redirectNewSignupApp(route);
      return of(false);
    } else {
      return of(true).pipe(
        filter((shouldNavigateToOldSignup) => shouldNavigateToOldSignup),
        concatMap(() => this.authService.isAuthenticatedAsync()),
        concatMap(isAuthenticated => {
          const appKey = route.queryParams['appkey'];
          const newSignup = route.queryParams['new_signup'] === 'true';

          if (appKey || newSignup) {
            this.authService.resetCredentials();
            return of(true);
          } else if (isAuthenticated) {
            return this.installationV2Navigation();
          } else {
            return of(true);
          }
        }),
        catchError(error => {
          console.log('error in account resolver - ' + JSON.stringify(error));
          return of(true);
        }));
    }
  }

  private installationV2Navigation(): Observable<boolean> {
    return this.featureService.getFeatureSettings('InstallationV2').pipe(map(
      (installationSettings) => {
        if (installationSettings.response.install_status === INSTALLATION_FINISH_STATUS) {
          this.redirectService.redirectTo(ENV.YotpoNewB2bEndpoint);
          return false;
        } else {
          this.router.navigateByUrl(installationSettings.response.step);
          return false;
        }
      }
    ));
  }

  private shouldRedirectToNewSignupApp(platform, product): boolean {
    if (['shopify', '1'].indexOf(platform) > -1) {
      return true;
    } else if (platform === 'bigcommerce' && product.toLowerCase() === 'loyalty') {
      return true;
    }
    return false;
  }

  private redirectNewSignupApp(route): void {
    const url = ENV.YotpoAccountsCompleteRegistrationUrl + this.router.createUrlTree([''], { queryParams: route.queryParams });
    this.redirectService.navigateOrRedirectTo(url);
  }
}
